import React, { useState, useRef, useEffect } from "react";
import logger from "../lib/logger";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eyebrow, Btn } from "../components/Primitives";
import { api } from "../lib/api";
import PaywallReviews from "./paywall/PaywallReviews";
import AccessCodeModal from "./paywall/AccessCodeModal";
import FoundingTermsModal from "./paywall/FoundingTermsModal";
import PayPalCheckout from "../components/PayPalCheckout";

const TILE_PULSE_ANIM = { opacity: [0.5, 0.8, 0.5] };

const DEFAULT_CFG = {
    founding_price: "$99",
    founding_label: "Founding 100",
    founding_caption: "Prepaid credit to your account",
    founding_blurb: "The first founding members get $99 of platform credit, locked in beta access. Your credit applies to any plan when we officially launch. Credit never expires.",
    discount_active: true,
    discount_banner: "Limited — only 100 founding spots, claimed in order.",
    discount_blurb: "Once the 100 are gone, founding pricing closes for good.",
};

export default function Paywall({ archetype, onSuccess, onDismiss, isDiscount = false, sessionId, onRedeemed, onPaid }) {
    const [cfg, setCfg] = useState(DEFAULT_CFG);
    const [submitting, setSubmitting] = useState(false);
    const [slideIdx, setSlideIdx] = useState(0);
    const [accepted, setAccepted] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [showCode, setShowCode] = useState(false);
    const [code, setCode] = useState("");
    const [redeeming, setRedeeming] = useState(false);
    const [codeError, setCodeError] = useState(null);
    const [founding, setFounding] = useState({ spots_remaining: 100, spots_claimed: 0, closed: false });
    const containerRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                const r = await fetch(`${BACKEND_URL}/api/config/pricing`);
                if (r.ok) {
                    const data = await r.json();
                    setCfg({ ...DEFAULT_CFG, ...data });
                }
            } catch (err) {
                logger.warn("pricing config fetch failed:", err);
            }
            try {
                const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                const r = await fetch(`${BACKEND_URL}/api/founding/stats`);
                if (r.ok) setFounding(await r.json());
            } catch (err) {
                logger.warn("founding stats fetch failed:", err);
            }
        })();
    }, []);

    const onScroll = () => {
        const el = containerRef.current;
        if (!el) return;
        const idx = Math.round(el.scrollTop / el.clientHeight);
        setSlideIdx(Math.min(2, Math.max(0, idx)));
    };

    const submit = async () => {
        if (!accepted) {
            setShowTerms(true);
            return;
        }
        setSubmitting(true);
        try {
            await api.acceptTerms(sessionId, "checkout");
            const res = await api.createCheckout(sessionId, window.location.origin);
            window.location.href = res.checkout_url;
        } catch (e) {
            logger.error("checkout start failed:", e);
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not start checkout. Please try again.");
            setSubmitting(false);
        }
    };

    const redeem = async () => {
        if (!sessionId) {
            setCodeError("Session not ready, please refresh");
            return;
        }
        const trimmed = code.trim();
        if (!trimmed) {
            setCodeError("Enter a code");
            return;
        }
        setRedeeming(true);
        setCodeError(null);
        try {
            const res = await api.redeemCode(sessionId, trimmed);
            toast.success(res.type === "demo" ? "Demo access unlocked" : "Access granted — welcome!");
            setShowCode(false);
            if (onRedeemed) {
                onRedeemed(res);
            } else {
                onSuccess("code", true);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setCodeError(typeof detail === "string" ? detail : "Could not redeem code");
        } finally {
            setRedeeming(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-cream relative" data-testid={isDiscount ? "paywall-discount" : "paywall-screen"}>
            {/* Top header */}
            <div className="px-6 pt-5 pb-3 flex items-center justify-between bg-cream z-30 relative">
                <button
                    data-testid="paywall-close-btn"
                    onClick={onDismiss}
                    className="text-[11px] tracking-[0.28em] uppercase text-ink-muted"
                    aria-label="Close paywall"
                >
                    Back
                </button>
                <div className="flex gap-1.5">
                    {[0, 1, 2].map(i => (
                        <span
                            key={`dot-${i}`}
                            className={`h-[2px] transition-all ${slideIdx === i ? "w-8 bg-burgundy" : "w-3 bg-burgundy/20"}`}
                        />
                    ))}
                </div>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    {String(slideIdx + 1).padStart(2, "0")}
                </span>
            </div>

            {isDiscount && (
                <div className="bg-burgundy text-cream text-center py-3 text-xs tracking-[0.24em] uppercase font-medium" data-testid="paywall-discount-banner">
                    {cfg.discount_banner}
                </div>
            )}

            {/* Vertical scroll snap slides */}
            <div
                ref={containerRef}
                onScroll={onScroll}
                className="flex-1 overflow-y-auto snap-y snap-mandatory no-scrollbar"
                style={{ paddingBottom: "calc(48vh)" }}
                data-testid="paywall-slides"
            >
                {/* Slide 1 — re-tease */}
                <section className="snap-start min-h-[52vh] flex flex-col justify-center px-7 py-10 border-b border-burgundy/10">
                    <Eyebrow>Your archetype</Eyebrow>
                    <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy">
                        <span className="italic">{archetype?.name?.split(" ").slice(0, 2).join(" ") || "Your"}</span>{" "}
                        {archetype?.name?.split(" ").slice(2).join(" ") || ""}
                    </h2>
                    <p className="mt-4 font-display italic text-lg text-ink-muted">
                        Your full profile is ready.
                    </p>
                    <div className="mt-8 grid grid-cols-3 gap-3">
                        {["3-paragraph profile", "Your ideal partner", "4 blind spots"].map((t) => (
                            <div key={t} className="border border-burgundy/15 p-3 text-center">
                                <div className="font-display text-xs italic text-burgundy">Included</div>
                                <div className="text-xs mt-1 text-ink leading-tight">{t}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Slide 2 — social proof */}
                <section className="snap-start min-h-[52vh] flex flex-col justify-center px-7 py-10 border-b border-burgundy/10 bg-cream-soft">
                    <Eyebrow>As seen on TikTok</Eyebrow>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-[1.1] text-burgundy">
                        <span className="italic">Thousands</span> of women have discovered their archetype.
                    </h2>
                    <div className="mt-7 grid grid-cols-3 gap-2">
                        {[1, 2, 3].map(i => (
                            <div
                                key={`tile-${i}`}
                                className="aspect-[9/16] bg-burgundy/5 border border-burgundy/15 relative overflow-hidden flex items-end p-2"
                            >
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-br from-rose/40 via-gold/30 to-burgundy/30"
                                    animate={TILE_PULSE_ANIM}
                                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.4 }}
                                />
                                <span className="relative font-display italic text-cream text-xs">
                                    "i can't unread this"
                                </span>
                            </div>
                        ))}
                    </div>
                    <p className="mt-5 text-xs tracking-wide text-ink-muted text-center">
                        Reactions from the Ever After waitlist community
                    </p>
                </section>

                {/* Slide 3 — reviews */}
                <PaywallReviews />
            </div>

            {/* Sticky Founding 100 offer */}
            <div className="absolute left-0 right-0 bottom-0 bg-cream/97 backdrop-blur-md border-t border-burgundy/15 z-40">
                <div className="px-6 py-5">
                    <div className="border border-burgundy/25 bg-cream-soft p-5" data-testid="founding-offer-card">
                        <div className="flex items-baseline justify-between gap-3">
                            <div>
                                <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">{cfg.founding_label}</div>
                                <div className="mt-1 font-display text-4xl text-burgundy leading-none">
                                    <span className="italic">{cfg.founding_price}</span>
                                </div>
                                <div className="mt-1 text-[10px] tracking-[0.24em] uppercase text-ink-muted">
                                    {cfg.founding_caption}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-display italic text-sm text-burgundy">Beta access</div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-ink-light">Credit never expires</div>
                            </div>
                        </div>
                        <p className="mt-4 font-display italic text-sm text-ink leading-snug">
                            {isDiscount && cfg.discount_blurb ? cfg.discount_blurb : cfg.founding_blurb}
                        </p>
                    </div>

                    <div className="mt-3">
                        <label className="flex items-start gap-2 mb-3 cursor-pointer" data-testid="terms-checkbox-row">
                            <input
                                data-testid="terms-checkbox"
                                type="checkbox"
                                checked={accepted}
                                onChange={e => setAccepted(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-burgundy shrink-0"
                            />
                            <span className="text-[11px] leading-snug text-ink">
                                I understand and agree to the{" "}
                                <button
                                    data-testid="open-terms-btn"
                                    type="button"
                                    onClick={() => setShowTerms(true)}
                                    className="underline underline-offset-2 text-burgundy"
                                >
                                    Founding Member Terms
                                </button>
                                .
                            </span>
                        </label>
                        <Btn
                            data-testid="paywall-cta"
                            onClick={submit}
                            disabled={submitting || founding.closed || !accepted}
                            className={(submitting || founding.closed || !accepted) ? "opacity-60" : ""}
                        >
                            {founding.closed
                                ? "Founding 100 is closed"
                                : submitting
                                    ? "Securing your spot…"
                                    : "Complete purchase — $99"}
                        </Btn>
                    </div>
                    {!founding.closed && (
                        <PayPalCheckout
                            sessionId={sessionId}
                            disabled={!accepted}
                            onBeforeOrder={() => api.acceptTerms(sessionId, "checkout")}
                            onPaid={(number) => onPaid?.(number)}
                        />
                    )}
                    <p className="mt-2 text-center text-[9px] tracking-[0.28em] uppercase text-ink-light">
                        {founding.spots_remaining} of {founding.spots_total || 100} spots remaining · 30-day refund
                    </p>
                    <div className="mt-3 text-center">
                        <button
                            data-testid="paywall-have-code-btn"
                            type="button"
                            onClick={() => { setShowCode(true); setCodeError(null); }}
                            className="text-[10px] tracking-[0.28em] uppercase text-burgundy/80 hover:text-burgundy underline underline-offset-4"
                        >
                            I have an access code
                        </button>
                    </div>
                </div>
            </div>

            {/* Founding Member Terms modal */}
            {showTerms && (
                <FoundingTermsModal
                    accepted={accepted}
                    setAccepted={setAccepted}
                    onClose={() => setShowTerms(false)}
                />
            )}

            {/* Access code modal */}
            {showCode && (
                <AccessCodeModal
                    code={code}
                    setCode={setCode}
                    codeError={codeError}
                    setCodeError={setCodeError}
                    redeem={redeem}
                    redeeming={redeeming}
                    onClose={() => setShowCode(false)}
                />
            )}
        </div>
    );
}

